/**
 * The tenant's recent audit events, and a way to ask for them again.
 *
 * @module components/admin/useAuditTrail
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { AuditQuery, ProgrammePlatformClient } from "@/platform";
import type { AuditEvent } from "@/server/types";
import { begin } from "@/utils/begin";

/** How many events one read asks the server for. */
export const AUDIT_PAGE_SIZE = 25;

export interface AuditTrailState {
  events: AuditEvent[];
  /** How many events match the filter, of which `events` is the newest page. */
  total: number;
  error: string | null;
  /** True until the first read answers, so an empty trail is not reported early. */
  pending: boolean;
  reload: () => Promise<void>;
}

/**
 * @param client - The platform client to read through.
 * @param filter - Narrows the read on the server; a change reads again.
 * @param limit - How many of the newest matching events to read.
 * @returns The trail, whether it is still arriving, and a reload for the
 * screens that write something the trail should then show.
 *
 * @example
 * ```tsx
 * const { events, error, pending, reload } = useAuditTrail(client);
 * ```
 */
export function useAuditTrail(
  client: ProgrammePlatformClient,
  filter: Omit<AuditQuery, "limit"> = {},
  limit = AUDIT_PAGE_SIZE,
): AuditTrailState {
  // Read field by field, so a caller rebuilding the filter object each render
  // does not re-read the trail each render.
  const { actor, action, programmeId, since, until } = filter;
  // Keyed by value, so a list rebuilt each render reads once.
  const actionsKey = filter.actions?.join("\n");
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Starts pending: the mount effect does the first read, and an empty trail
  // before it answers is not a tenant with no audit events.
  const [pending, setPending] = useState(true);
  // Only the newest read may write: an earlier filter's answer can land last.
  const generation = useRef(0);

  // A promise chain rather than async/await, so every state update lands in a
  // continuation and the mount effect never cascades a render.
  const reload = useCallback(
    (): Promise<void> =>
      // Raised here and not only at mount: Try again is pressed on a screen
      // holding nothing but the failure, so a read that does not say it
      // started changes nothing, and nothing at all when it fails again.
      begin(() => {
        generation.current += 1;
        const mine = generation.current;
        setPending(true);
        // Cleared as the read starts, as `useProviderSettings.reload` does:
        // the failure being retried is no longer what the screen is showing.
        setError(null);
        const narrowing = {
          ...(actor ? { actor } : {}),
          ...(action ? { action } : {}),
          ...(actionsKey === undefined ? {} : { actions: actionsKey.split("\n") }),
          ...(programmeId ? { programmeId } : {}),
          ...(since ? { since } : {}),
          ...(until ? { until } : {}),
        };
        return Promise.all([
          client.listAuditEvents({ ...narrowing, limit }),
          client.countAuditEvents(narrowing),
        ]).then(
          ([next, count]) => {
            if (mine !== generation.current) {
              return;
            }
            setEvents(next);
            setTotal(count);
            setError(null);
            setPending(false);
          },
          (cause: unknown) => {
            if (mine !== generation.current) {
              return;
            }
            setError(cause instanceof Error ? cause.message : "Unable to load the audit trail");
            setPending(false);
          },
        );
      }),
    [client, actor, action, actionsKey, programmeId, since, until, limit],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  return { events, total, error, pending, reload };
}
